import {
  Application,
  Assets,
  BlurFilter,
  Container,
  Graphics,
  Sprite,
  Text,
  Texture,
} from "pixi.js";
import { getDefaultStore } from "jotai";

import {
  activeAlertsAtom,
  type ActiveAlertInstance,
} from "../../../atoms/atomsAlertRuntime";
import { atomStudio } from "../../../screens/Dashboard/SubComponents/Studio/Studio.atoms";
import { normalizeAlertSlotBounds } from "../../../screens/Dashboard/SubComponents/Studio/utils/alertSlot.utils";
import type { StreamEvent } from "../../../services/eventBus/types";
import type {
  IAlertAnimationKind,
  IStudioAlert,
} from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Alert.interface";
import type {
  IOverlayElement,
  IOverlayElementImage,
  IOverlayElementVideo,
  IOverlayElementShape,
  IOverlayElementText,
  IOverlayFill,
} from "../../../screens/Dashboard/SubComponents/Studio/interfaces/sub-interfaces/Overlay.interface";
import { resolveAlertDesignSize } from "../../composition";

import DrawCache from "./DrawCache";

const STAGE_W = 1920;
const STAGE_H = 1080;

/** Default slot bounds in percent — top-right quadrant. Matches the
 *  `AlertSlot` editor default so DOM editor and Pixi runtime agree. */
const DEFAULT_SLOT_BOUNDS = {
  left: 60,
  top: 5,
  width: 35,
  height: 25,
  zIndex: 100,
};

const DEFAULT_LIFECYCLE = {
  enterMs: 600,
  holdMs: 4000,
  exitMs: 600,
} as const;

/**
 * Build the `{ last: { follow|sub|donation|raid: {...} } }` variables map
 * from a single StreamEvent. Mirrors `useLastEventVars` so templates
 * referencing `{last.<kind>.username}` etc. resolve identically when fired
 * via the editor's "Simulate" action or by a real bus event.
 */
const buildEventVars = (event: StreamEvent): Record<string, unknown> => {
  const base = {
    follow: {
      username: "",
      displayName: "",
      avatar: "",
      platform: "",
      timestamp: 0,
    },
    sub: {
      username: "",
      displayName: "",
      tier: "",
      months: 0,
      isGift: false,
      gifterName: "",
      message: "",
      platform: "",
      timestamp: 0,
    },
    donation: {
      username: "",
      displayName: "",
      amount: 0,
      currency: "",
      amountUsd: 0,
      message: "",
      platform: "",
      timestamp: 0,
    },
    raid: {
      username: "",
      displayName: "",
      viewers: 0,
      platform: "",
      timestamp: 0,
    },
  } as Record<string, Record<string, unknown>>;

  const slot = event.kind;
  base[slot] = {
    ...base[slot],
    username: event.user?.username ?? "",
    displayName: event.user?.displayName ?? "",
    avatar: event.user?.avatar ?? "",
    platform: event.platform ?? "",
    timestamp: event.timestamp ?? 0,
    ...(event.kind === "sub"
      ? {
          tier: event.tier ?? "",
          months: event.months ?? 0,
          isGift: event.isGift ?? false,
          gifterName: event.gifterName ?? "",
          message: event.message ?? "",
        }
      : {}),
    ...(event.kind === "donation"
      ? {
          amount: event.amount ?? 0,
          currency: event.currency ?? "",
          amountUsd: event.amountUsd ?? 0,
          message: event.message ?? "",
        }
      : {}),
    ...(event.kind === "raid"
      ? {
          viewers: event.viewers ?? 0,
        }
      : {}),
  };

  return { last: base };
};

/** `{a.b.c}` → vars.a.b.c (or undefined). */
const resolvePath = (vars: Record<string, unknown>, path: string): unknown => {
  return path.split(".").reduce<unknown>((acc, key) => {
    if (acc == null || typeof acc !== "object") return undefined;
    return (acc as Record<string, unknown>)[key];
  }, vars);
};

const interpolate = (
  template: string,
  vars: Record<string, unknown>,
): string => {
  if (!template) return template;
  return template.replace(
    /\{([a-zA-Z_$][\w$]*(?:\.[a-zA-Z_$][\w$]*)*)\}/g,
    (match, path: string) => {
      const value = resolvePath(vars, path);
      if (value === undefined) return match;
      if (value === null) return "";
      return String(value);
    },
  );
};

/** Reduce an `IOverlayFill` (color string OR gradient) to a single Pixi
 *  color. Pixi v8 has gradient support but it's expensive to wire per
 *  shape — for v1 we collapse gradients to their first stop, which keeps
 *  alerts visible without the runtime/editor diverging too hard. */
const resolveFill = (
  fill: IOverlayElementShape["options"]["fill"] | undefined,
): { color: number; alpha: number } | null => {
  if (!fill) return null;
  if (typeof fill === "string") return parseColor(fill);
  const f = fill as IOverlayFill;
  if (f.kind === "solid") return parseColor(f.color);
  if (f.stops && f.stops.length > 0) return parseColor(f.stops[0].color);
  return null;
};

const HEX_RE = /^#?([0-9a-f]{6}|[0-9a-f]{8}|[0-9a-f]{3})$/i;
const RGBA_RE =
  /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+))?\s*\)$/i;

const parseColor = (input: string): { color: number; alpha: number } => {
  const s = input.trim();
  const hex = s.match(HEX_RE);
  if (hex) {
    let h = hex[1];
    if (h.length === 3)
      h = h
        .split("")
        .map((c) => c + c)
        .join("");
    const color = parseInt(h.slice(0, 6), 16);
    const alpha = h.length === 8 ? parseInt(h.slice(6, 8), 16) / 255 : 1;
    return { color, alpha };
  }
  const rgba = s.match(RGBA_RE);
  if (rgba) {
    const r = parseInt(rgba[1], 10);
    const g = parseInt(rgba[2], 10);
    const b = parseInt(rgba[3], 10);
    const a = rgba[4] !== undefined ? parseFloat(rgba[4]) : 1;
    return { color: (r << 16) | (g << 8) | b, alpha: a };
  }
  return { color: 0xffffff, alpha: 1 };
};

const drawShape = (
  g: Graphics,
  spec: IOverlayElementShape,
  width: number,
  height: number,
): void => {
  const { shape, borderRadius = 0, stroke, strokeWidth } = spec.options;
  const fill = resolveFill(spec.options.fill);

  switch (shape) {
    case "rect":
      g.rect(0, 0, width, height);
      break;
    case "rounded-rect":
      g.roundRect(0, 0, width, height, borderRadius);
      break;
    case "circle":
      g.ellipse(width / 2, height / 2, width / 2, height / 2);
      break;
    case "hexagon": {
      // Pointy-top hexagon inscribed in the bounds.
      const cx = width / 2;
      const cy = height / 2;
      const rx = width / 2;
      const ry = height / 2;
      const pts: number[] = [];
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i - Math.PI / 2;
        pts.push(cx + rx * Math.cos(a), cy + ry * Math.sin(a));
      }
      g.poly(pts);
      break;
    }
    case "chevron": {
      // Right-pointing chevron with 50% notch.
      const notch = width * 0.5;
      g.poly([
        0,
        0,
        width - notch,
        0,
        width,
        height / 2,
        width - notch,
        height,
        0,
        height,
        notch,
        height / 2,
      ]);
      break;
    }
    case "parallelogram": {
      const skew = width * 0.2;
      g.poly([skew, 0, width, 0, width - skew, height, 0, height]);
      break;
    }
    default:
      g.rect(0, 0, width, height);
  }

  if (fill) g.fill({ color: fill.color, alpha: fill.alpha });
  if (stroke && strokeWidth && strokeWidth > 0) {
    const sc = parseColor(stroke);
    g.stroke({
      color: sc.color,
      alpha: sc.alpha,
      width: strokeWidth,
    });
  }
};

const clamp01 = (n: number) => Math.max(0, Math.min(1, n));

// ── Animaciones de enter/exit (transform real, no solo alpha) ────────────────────────
type AnimFrom = {
  dx?: number; // offset inicial X en fracción del slot
  dy?: number; // offset inicial Y en fracción del slot
  sx?: number; // escala inicial X (1 = identidad)
  sy?: number; // escala inicial Y
  rot?: number; // rotación inicial en grados
  blur?: number; // blur inicial en px
};
type Easing = (t: number) => number;

const easeOutCubic: Easing = (t) => 1 - Math.pow(1 - t, 3);
const easeOutBack: Easing = (t) => {
  const c1 = 1.70158;
  const c3 = c1 + 1;
  return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
};
const easeOutElastic: Easing = (t) => {
  if (t === 0 || t === 1) return t;
  const c4 = (2 * Math.PI) / 3;
  return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
};
const easeOutBounce: Easing = (t) => {
  const n1 = 7.5625;
  const d1 = 2.75;
  if (t < 1 / d1) return n1 * t * t;
  if (t < 2 / d1) return n1 * (t -= 1.5 / d1) * t + 0.75;
  if (t < 2.5 / d1) return n1 * (t -= 2.25 / d1) * t + 0.9375;
  return n1 * (t -= 2.625 / d1) * t + 0.984375;
};

const DEG = Math.PI / 180;

const ALERT_ANIM: Record<
  IAlertAnimationKind,
  { from: AnimFrom; ease: Easing }
> = {
  fade: { from: {}, ease: easeOutCubic },
  "slide-in-top": { from: { dy: -0.6 }, ease: easeOutCubic },
  "slide-in-right": { from: { dx: 0.6 }, ease: easeOutCubic },
  "slide-in-bottom": { from: { dy: 0.6 }, ease: easeOutCubic },
  "slide-in-left": { from: { dx: -0.6 }, ease: easeOutCubic },
  scale: { from: { sx: 0.6, sy: 0.6 }, ease: easeOutCubic },
  pop: { from: { sx: 0.7, sy: 0.7 }, ease: easeOutBack },
  "slide-fade-top": { from: { dy: -0.3 }, ease: easeOutCubic },
  "slide-fade-right": { from: { dx: 0.3 }, ease: easeOutCubic },
  "slide-fade-bottom": { from: { dy: 0.3 }, ease: easeOutCubic },
  "slide-fade-left": { from: { dx: -0.3 }, ease: easeOutCubic },
  "zoom-blur": { from: { sx: 1.4, sy: 1.4, blur: 14 }, ease: easeOutCubic },
  "flip-x": { from: { sx: 0 }, ease: easeOutCubic },
  "flip-y": { from: { sy: 0 }, ease: easeOutCubic },
  "bounce-in": { from: { sx: 0.3, sy: 0.3 }, ease: easeOutBounce },
  "elastic-left": { from: { dx: -0.6 }, ease: easeOutElastic },
  "elastic-right": { from: { dx: 0.6 }, ease: easeOutElastic },
  "swing-in-top": { from: { dy: -0.5, rot: -12 }, ease: easeOutBack },
};

/** Progreso 0–1 DENTRO de la etapa actual. startedAt NO se resetea por etapa, así que el
 *  progreso de exit se calcula relativo a sus límites (esto además arregla el fade de salida,
 *  que antes usaba elapsed total → salía instantáneo). */
const computeStageProgress = (
  instance: ActiveAlertInstance,
  now: number,
): { stage: "enter" | "hold" | "exit"; t: number } => {
  const lc = instance.alert.lifecycle ?? DEFAULT_LIFECYCLE;
  const enterMs = lc.enterMs ?? DEFAULT_LIFECYCLE.enterMs;
  const holdMs = lc.holdMs ?? DEFAULT_LIFECYCLE.holdMs;
  const exitMs = lc.exitMs ?? DEFAULT_LIFECYCLE.exitMs;
  const elapsed = now - instance.startedAt;

  if (instance.stage === "exit") {
    return {
      stage: "exit",
      t: clamp01((elapsed - enterMs - holdMs) / Math.max(1, exitMs)),
    };
  }
  if (instance.stage === "enter") {
    return { stage: "enter", t: clamp01(elapsed / Math.max(1, enterMs)) };
  }
  return { stage: "hold", t: 1 };
};

const computeStageAlpha = (
  instance: ActiveAlertInstance,
  now: number,
): number => {
  const { stage, t } = computeStageProgress(instance, now);
  if (stage === "enter") return t;
  if (stage === "exit") return 1 - t;
  return 1;
};

/** Transform de la etapa: offsets (fracción de slot), escala, rotación (rad) y blur (px).
 *  Enter anima `from → identidad`; exit anima `identidad → from(exitKind)`. */
const computeStageTransform = (
  instance: ActiveAlertInstance,
  now: number,
): {
  ox: number;
  oy: number;
  sx: number;
  sy: number;
  rot: number;
  blur: number;
} => {
  const { stage, t } = computeStageProgress(instance, now);
  if (stage === "hold") return { ox: 0, oy: 0, sx: 1, sy: 1, rot: 0, blur: 0 };

  const lc = instance.alert.lifecycle ?? {};
  const enterKind = lc.enter ?? "fade";
  const kind = stage === "enter" ? enterKind : (lc.exit ?? enterKind);
  const spec = ALERT_ANIM[kind] ?? ALERT_ANIM.fade;
  const e = spec.ease(t);
  // enter: factor 1→0 (llega a identidad). exit: factor 0→1 (parte de identidad).
  const f = stage === "enter" ? 1 - e : e;
  const from = spec.from;

  return {
    ox: (from.dx ?? 0) * f,
    oy: (from.dy ?? 0) * f,
    sx: 1 + ((from.sx ?? 1) - 1) * f,
    sy: 1 + ((from.sy ?? 1) - 1) * f,
    rot: (from.rot ?? 0) * DEG * f,
    blur: (from.blur ?? 0) * f,
  };
};

/** Per-alert-container pool of rendered children. Lets the per-frame ticker
 *  reuse Graphics/Text instead of re-allocating them every frame; children are
 *  only rebuilt when the element count or the geometry signature changes. */
interface AlertElementPool {
  children: (Graphics | Text | Sprite | null)[];
  count: number;
  geomSig: string;
}

class AlertsFactory {
  private App: Application;
  private DrawCache: DrawCache;
  private Root: Container;
  private store = getDefaultStore();
  private tickerCallback: (() => void) | null = null;
  // Cuando la escena está oculta (pool keep-alive) pausamos el callback del ticker
  // para no correr renderAlerts en un árbol invisible (regla de fps).
  private tickerPaused = false;
  private lastWasEmpty = true;
  private elementPools = new Map<string, AlertElementPool>();
  private lastStudioRef: unknown = null;
  private alertIndex = new Map<string, IStudioAlert>();
  // BlurFilter por alert activo (solo zoom-blur lo usa, durante enter/exit). Se monta/desmonta
  // según el blur calculado para no dejar un filtro permanente caro en el composite.
  private blurFilters = new Map<string, BlurFilter>();
  // Cache de texturas de imagen por src (compartida entre instancias y frames).
  private imageTextures = new Map<string, Texture>();

  private videoTextures = new Map<string, Texture>();

  constructor(app: Application, drawCache: DrawCache, root: Container) {
    this.App = app;
    this.DrawCache = drawCache;
    this.Root = root;
    // Hook into Pixi's shared ticker so renderAlerts runs every frame —
    // the engine drives `activeAlertsAtom` asynchronously, but Pixi's
    // canvas only re-paints when `App.ticker` fires. Without this hook,
    // testFire would update state but the canvas would never reflect it.
    this.tickerCallback = () => {
      const instances = this.store.get(activeAlertsAtom);
      if (instances.length === 0) {
        if (this.lastWasEmpty) return;
        this.lastWasEmpty = true;
        // Final pass to clean up containers from the previous frame.
        void this.renderAlerts();
        return;
      }
      this.lastWasEmpty = false;
      void this.renderAlerts();
    };
    this.App.ticker.add(this.tickerCallback);
  }

  /** Render every active alert instance for the current frame. Existing
   *  containers are reused when the same `instanceId` is still active —
   *  only their alpha/lifecycle state updates each tick. Stale containers
   *  (instances no longer in the atom) get torn down. */
  async renderAlerts(): Promise<void> {
    const instances = this.store.get(activeAlertsAtom);
    const aliveKeys = new Set<string>();

    const now = Date.now();
    for (const instance of instances) {
      const key = `alert_${instance.instanceId}`;
      aliveKeys.add(key);
      this.renderInstance(key, instance, now);
    }

    // Clean up containers whose instances are no longer active.
    let removed = 0;
    for (const key in this.DrawCache.Containers) {
      if (key.startsWith("alert_") && !aliveKeys.has(key)) {
        const c = this.DrawCache.Containers[key];
        if (c && !c.destroyed) {
          c.removeChildren();
          c.destroy();
        }
        delete this.DrawCache.Containers[key];
        this.elementPools.delete(key);
        this.blurFilters.delete(key);
        removed += 1;
      }
    }
    if (instances.length > 0 || removed > 0) {
      // Sample once when state actually changes — avoid 60fps spam.
      if (this.lastLoggedActive !== instances.length) {
        console.log(
          `[AlertsFactory] renderAlerts active=${instances.length} removed=${removed}`,
        );
        this.lastLoggedActive = instances.length;
      }
    }
  }

  private lastLoggedActive = -1;

  private renderInstance(
    key: string,
    instance: ActiveAlertInstance,
    now: number,
  ): void {
    // Re-read the slot every frame from the studio atom: the user may be
    // dragging/resizing this alert in the editor right now, and we want the
    // running instance to follow the move. Fall back to the snapshot held
    // by the instance if the alert was deleted mid-fire.
    //
    // The lookup is indexed by id and rebuilt only when the studio atom
    // reference changes — avoids an O(n) `.find()` over all alerts per frame.
    const studio = this.store.get(atomStudio);
    if (studio !== this.lastStudioRef) {
      this.lastStudioRef = studio;
      this.alertIndex.clear();
      for (const a of studio?.alerts ?? []) {
        this.alertIndex.set(a.id, a);
      }
    }
    const liveAlert = this.alertIndex.get(instance.alert.id);
    const slot = liveAlert?.slot ??
      instance.alert.slot ?? { bounds: DEFAULT_SLOT_BOUNDS };
    // `liveAlert` is already healed by the atomStudio getter, but the snapshot
    // fallback (`instance.alert.slot`) is frozen at fire time and bypasses it —
    // a legacy alert still holding raw design-space px would render ~19x the
    // stage and land off-screen. Normalize here so both lanes agree. Idempotent
    // on already-% bounds (returns the same reference).
    const bounds = normalizeAlertSlotBounds(slot.bounds) ?? DEFAULT_SLOT_BOUNDS;

    const slotW = (bounds.width / 100) * STAGE_W;
    const slotH = (bounds.height / 100) * STAGE_H;
    const slotX = (bounds.left / 100) * STAGE_W;
    const slotY = (bounds.top / 100) * STAGE_H;

    let container = this.DrawCache.Containers[key];
    if (!container) {
      container = new Container();
      container.label = key;
      this.DrawCache.Containers[key] = container;
      this.Root.addChild(container);
      console.log(
        `[AlertsFactory] renderInstance MOUNT ${key} bounds=${slotX.toFixed(0)},${slotY.toFixed(0)} ${slotW.toFixed(0)}x${slotH.toFixed(0)} elements=${instance.alert.composition.elements.length}`,
      );
    }

    // Transform de la animación de etapa (enter/exit). Pivot al centro del slot → scale/rotación
    // alrededor del centro; la posición compensa el pivot, así el layout base no se mueve.
    const tf = computeStageTransform(instance, now);
    container.pivot.set(slotW / 2, slotH / 2);
    container.position.set(
      slotX + slotW / 2 + tf.ox * slotW,
      slotY + slotH / 2 + tf.oy * slotH,
    );
    container.scale.set(tf.sx, tf.sy);
    container.rotation = tf.rot;
    // Assigning zIndex marks App.stage.sortDirty internally (Pixi sort mixin),
    // so only touch it when it actually changes — no unconditional re-sort.
    const zIndex = bounds.zIndex ?? 100;
    if (container.zIndex !== zIndex) {
      container.zIndex = zIndex;
    }
    container.alpha = computeStageAlpha(instance, now);
    this.applyBlur(key, container, tf.blur);

    // Variables for `{last.<kind>.*}` interpolation. Constant per instance
    // (the event never changes), so pooled text never needs re-interpolation.
    const vars = buildEventVars(instance.event);

    const designSize = resolveAlertDesignSize(instance.alert.composition);
    const sx = slotW / designSize.width;
    const sy = slotH / designSize.height;

    const elements = instance.alert.composition.elements;
    const geomSig = `${sx}:${sy}`;
    let pool = this.elementPools.get(key);

    // Rebuild children only when the pool is missing, the element count
    // changed, or the geometry changed (e.g. the slot was resized). In the
    // common per-frame case nothing below runs — only alpha/position above.
    if (!pool || pool.count !== elements.length || pool.geomSig !== geomSig) {
      if (pool) {
        for (const child of pool.children) child?.destroy();
      }
      container.removeChildren();

      const children: (Graphics | Text | Sprite | null)[] = [];
      for (const el of elements) {
        const child = this.createElement(el, vars, sx, sy);
        if (child) container.addChild(child);
        children.push(child);
      }

      pool = { children, count: elements.length, geomSig };
      this.elementPools.set(key, pool);
    }
  }

  /** Monta/desmonta un BlurFilter en el container según el blur de la animación (solo zoom-blur).
   *  Evita dejar un filtro permanente: se quita cuando el blur llega a ~0. */
  private applyBlur(key: string, container: Container, blur: number): void {
    if (blur > 0.1) {
      let bf = this.blurFilters.get(key);
      if (!bf) {
        bf = new BlurFilter({ strength: blur });
        this.blurFilters.set(key, bf);
      }
      bf.strength = blur;
      const filters = container.filters as unknown[] | null;
      if (!filters || filters.length !== 1) {
        container.filters = [bf];
      }
    } else if (this.blurFilters.has(key)) {
      this.blurFilters.delete(key);
      container.filters = [];
    }
  }

  /** Carga (y cachea por src) la textura de una imagen de la composición. Soporta http(s)/data/blob
   *  y URLs externas vía Assets.load. Devuelve null si falla (se descarta el elemento, sin romper). */
  private async loadImageTexture(src: string): Promise<Texture | null> {
    const cached = this.imageTextures.get(src);
    if (cached) return cached;
    try {
      const tex = (await Assets.load(src)) as Texture;
      this.imageTextures.set(src, tex);
      return tex;
    } catch (e) {
      console.warn(`[AlertsFactory] image load failed: ${src}`, e);
      return null;
    }
  }

  private loadVideoTexture(
    src: string,
    loop: boolean,
    muted: boolean,
  ): Promise<Texture | null> {
    const cached = this.videoTextures.get(src);
    if (cached) return Promise.resolve(cached);
    return new Promise((resolve) => {
      const video = document.createElement("video");
      video.crossOrigin = "anonymous";
      video.loop = loop;
      video.muted = muted;
      video.autoplay = true;
      video.playsInline = true;
      video.onloadedmetadata = () => {
        try {
          const tex = Texture.from(video);
          void video.play().catch(() => {});
          this.videoTextures.set(src, tex);
          resolve(tex);
        } catch {
          resolve(null);
        }
      };
      video.onerror = () => resolve(null);
      video.src = src;
    });
  }

  private createElement(
    el: IOverlayElement,
    vars: Record<string, unknown>,
    sx: number,
    sy: number,
  ): Graphics | Text | Sprite | null {
    if (el.kind === "shape") {
      const shape = el as IOverlayElementShape;
      const w = shape.bounds.width * sx;
      const h = shape.bounds.height * sy;
      const x = shape.bounds.left * sx;
      const y = shape.bounds.top * sy;

      const g = new Graphics();
      g.position.set(x, y);
      drawShape(g, shape, w, h);
      g.zIndex = shape.bounds.zIndex ?? 0;
      if (shape.rotation) {
        g.pivot.set(w / 2, h / 2);
        g.position.set(x + w / 2, y + h / 2);
        g.rotation = (shape.rotation * Math.PI) / 180;
      }
      return g;
    }

    if (el.kind === "text") {
      const t = el as IOverlayElementText;
      const w = t.bounds.width * sx;
      const h = t.bounds.height * sy;
      const x = t.bounds.left * sx;
      const y = t.bounds.top * sy;

      const fontSize = parseInt(t.options.fontSize ?? "24", 10) * sy;
      const colorParsed = parseColor(t.options.fontColor ?? "#ffffff");
      const align = t.options.textAlign ?? "left";

      const text = new Text({
        text: interpolate(t.options.text ?? "", vars),
        style: {
          fontFamily: "Roboto, system-ui, sans-serif",
          fontSize,
          fill: colorParsed.color,
          align,
          fontWeight: t.options.bold ? "bold" : "normal",
          fontStyle: t.options.italic ? "italic" : "normal",
          wordWrap: true,
          wordWrapWidth: w,
        },
      });
      text.alpha = colorParsed.alpha;
      text.anchor.y = 0.5;
      text.y = y + h / 2;
      if (align === "center") {
        text.anchor.x = 0.5;
        text.x = x + w / 2;
      } else if (align === "right") {
        text.anchor.x = 1;
        text.x = x + w;
      } else {
        text.anchor.x = 0;
        text.x = x;
      }
      text.zIndex = t.bounds.zIndex ?? 0;
      return text;
    }

    if (el.kind === "image") {
      const img = el as IOverlayElementImage;
      const src = img.options?.src;
      if (!src) return null;

      const w = img.bounds.width * sx;
      const h = img.bounds.height * sy;
      const x = img.bounds.left * sx;
      const y = img.bounds.top * sy;

      // Tamaño/posición se aplican una vez que la textura existe (con Texture.EMPTY el
      // width/height no escala bien). Sprite persiste en el pool → al cargar async se swapea.
      const applySize = (s: Sprite) => {
        s.width = w;
        s.height = h;
        if (img.rotation) {
          s.anchor.set(0.5);
          s.position.set(x + w / 2, y + h / 2);
          s.rotation = (img.rotation * Math.PI) / 180;
        } else {
          s.anchor.set(0);
          s.position.set(x, y);
        }
      };

      const cached = this.imageTextures.get(src);
      const sprite = new Sprite(cached ?? Texture.EMPTY);
      sprite.zIndex = img.bounds.zIndex ?? 0;

      if (cached) {
        applySize(sprite);
      } else {
        void this.loadImageTexture(src).then((tex) => {
          if (tex && !sprite.destroyed) {
            sprite.texture = tex;
            applySize(sprite);
          }
        });
      }
      return sprite;
    }

    if (el.kind === "video") {
      const vid = el as IOverlayElementVideo;
      const src = vid.options?.src;
      if (!src) return null;

      const w = vid.bounds.width * sx;
      const h = vid.bounds.height * sy;
      const x = vid.bounds.left * sx;
      const y = vid.bounds.top * sy;

      const applySize = (s: Sprite) => {
        s.width = w;
        s.height = h;
        if (vid.rotation) {
          s.anchor.set(0.5);
          s.position.set(x + w / 2, y + h / 2);
          s.rotation = (vid.rotation * Math.PI) / 180;
        } else {
          s.anchor.set(0);
          s.position.set(x, y);
        }
      };

      const cached = this.videoTextures.get(src);
      const sprite = new Sprite(cached ?? Texture.EMPTY);
      sprite.zIndex = vid.bounds.zIndex ?? 0;

      if (cached) {
        applySize(sprite);
      } else {
        void this.loadVideoTexture(
          src,
          vid.options.loop !== false,
          vid.options.muted !== false,
        ).then((tex) => {
          if (tex && !sprite.destroyed) {
            sprite.texture = tex;
            applySize(sprite);
          }
        });
      }
      return sprite;
    }

    return null;
  }

  /** Pausa el callback del ticker (escena oculta en el pool keep-alive). Idempotente. */
  pause(): void {
    if (this.tickerPaused || !this.tickerCallback) return;
    try {
      this.App.ticker.remove(this.tickerCallback);
    } catch {
      /* App may already be destroyed */
    }
    this.tickerPaused = true;
  }

  /** Reanuda el callback del ticker (escena vuelve a ser visible). Idempotente. */
  resume(): void {
    if (!this.tickerPaused || !this.tickerCallback) return;
    this.App.ticker.add(this.tickerCallback);
    this.tickerPaused = false;
  }

  destroy(): void {
    if (this.tickerCallback) {
      try {
        this.App.ticker.remove(this.tickerCallback);
      } catch {
        /* App may already be destroyed */
      }
      this.tickerCallback = null;
    }
    for (const key in this.DrawCache.Containers) {
      if (!key.startsWith("alert_")) continue;
      const c = this.DrawCache.Containers[key];
      if (c && !c.destroyed) {
        c.removeChildren();
        try {
          c.destroy();
        } catch {
          /* ignore */
        }
      }
      delete this.DrawCache.Containers[key];
    }
    this.elementPools.clear();
    this.alertIndex.clear();
    this.blurFilters.clear();
    this.imageTextures.clear();
    this.videoTextures.clear();
    this.lastStudioRef = null;
  }
}

export default AlertsFactory;
